import i18n from 'i18next';
import { initReactI18next, useTranslation } from 'react-i18next';
import { isUiLocale, matchUiLocale, uiLocaleInfo, type UiLocale } from '@dubforge/shared';
import { ar } from './locales/ar';
import { de } from './locales/de';
import { en, type ContractorMessages } from './locales/en';
import { es419 } from './locales/es-419';
import { esES } from './locales/es-ES';
import { fr } from './locales/fr';
import { hi } from './locales/hi';
import { id } from './locales/id';
import { it } from './locales/it';
import { ja } from './locales/ja';
import { ko } from './locales/ko';
import { ptBR } from './locales/pt-BR';
import { ru } from './locales/ru';
import { tr } from './locales/tr';
import { zhHans } from './locales/zh-Hans';

/**
 * Interface translations. Everything a contractor can see is translated into every UI_LOCALE
 * (enforced by the ContractorMessages type). Admin-only screens live under `admin` and are English
 * only: other languages fall back to English for those keys.
 */
const translations: Record<UiLocale, ContractorMessages> = {
  en,
  'es-419': es419,
  'es-ES': esES,
  'pt-BR': ptBR,
  fr,
  de,
  it,
  ja,
  ko,
  'zh-Hans': zhHans,
  ru,
  hi,
  id,
  tr,
  ar,
};

const STORAGE_KEY = 'dubforge.locale';

function storedLocale(): UiLocale | null {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return isUiLocale(value) ? value : null;
  } catch {
    return null;
  }
}

/** Language to show before we know the user's saved choice: last used on this device, else the browser's. */
export function initialLocale(): UiLocale {
  return storedLocale() ?? matchUiLocale(navigator.languages ?? [navigator.language]);
}

function applyDocumentLocale(locale: UiLocale) {
  document.documentElement.lang = locale;
  document.documentElement.dir = uiLocaleInfo(locale).dir;
}

void i18n.use(initReactI18next).init({
  resources: Object.fromEntries(
    Object.entries(translations).map(([code, messages]) => [code, { translation: messages }]),
  ),
  lng: initialLocale(),
  fallbackLng: 'en',
  interpolation: { escapeValue: false }, // React escapes already
  returnNull: false,
});
applyDocumentLocale(i18n.language as UiLocale);

/** Switches the interface language on this device. Saving it to the account is the caller's job. */
export async function setLocale(locale: UiLocale) {
  try {
    localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    // Not persisted on this device; the account setting still applies after sign-in.
  }
  applyDocumentLocale(locale);
  await i18n.changeLanguage(locale);
}

/** Current interface language, re-rendering when it changes. */
export function useLocale(): UiLocale {
  const { i18n: instance } = useTranslation();
  return (isUiLocale(instance.language) ? instance.language : 'en') as UiLocale;
}

export { i18n };
